import { useEffect } from 'react';
import { useForm, FormProvider } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  CircularProgress,
} from '@mui/material';
import { ExpandMore as ExpandMoreIcon } from '@mui/icons-material';
import type { FormDefinition } from '../../../types/form-schema.types';
import { generateZodSchema } from './schema-generator';
import { FieldRenderer } from './FieldRenderer';

interface DynamicFormProps {
  formDefinition: FormDefinition;
  initialValues?: Record<string, unknown>;
  onSubmit: (data: Record<string, unknown>) => void;
  submitLabel?: string;
  isSubmitting?: boolean;
  lang?: string;
}

export function DynamicForm({
  formDefinition,
  initialValues = {},
  onSubmit,
  submitLabel = 'Absenden',
  isSubmitting = false,
  lang = 'de',
}: DynamicFormProps) {
  const schema = generateZodSchema(formDefinition);

  const methods = useForm({
    resolver: zodResolver(schema),
    defaultValues: initialValues,
    mode: 'onBlur',
  });

  useEffect(() => {
    if (initialValues && Object.keys(initialValues).length > 0) {
      methods.reset(initialValues);
    }
  }, [initialValues, methods]);

  const handleSubmit = methods.handleSubmit((data) => {
    onSubmit(data);
  });

  return (
    <FormProvider {...methods}>
      <Box component="form" onSubmit={handleSubmit}>
        {formDefinition.sections.map((section) => {
          const title = section.titles[lang] || section.titles['de'] || section.id;
          const description = section.descriptions[lang] || section.descriptions['de'];

          if (section.collapsible) {
            return (
              <Accordion
                key={section.id}
                defaultExpanded={!section.collapsed}
                sx={{ mb: 2 }}
              >
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography variant="h6">{title}</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  {description && (
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                      {description}
                    </Typography>
                  )}
                  {section.fields.map((field) => (
                    <FieldRenderer key={field.id} field={field} lang={lang} />
                  ))}
                </AccordionDetails>
              </Accordion>
            );
          }

          return (
            <Card key={section.id} sx={{ mb: 2 }}>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  {title}
                </Typography>
                {description && (
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    {description}
                  </Typography>
                )}
                {section.fields.map((field) => (
                  <FieldRenderer key={field.id} field={field} lang={lang} />
                ))}
              </CardContent>
            </Card>
          );
        })}

        <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end' }}>
          <Button
            type="submit"
            variant="contained"
            size="large"
            disabled={isSubmitting}
            startIcon={isSubmitting ? <CircularProgress size={20} /> : null}
          >
            {submitLabel}
          </Button>
        </Box>
      </Box>
    </FormProvider>
  );
}
